import ConfirmModal from "@app/components/confirm-modal";
import CreateAdminModal from "@app/components/create-admin-modal";
import CreateUserModal from "@app/components/create-user-modal";
import Table, {
  ColumnConfig,
  Order,
} from "@app/components/table/table.component";
import TitleDescription from "@app/components/title-description";
import { addToast } from "@app/components/toast/toast.service";
import {
  DEBOUNCE_TIMER,
  DEFAULT_PAGE,
  DEFAULT_REQUEST_PARAMS,
  OrderType,
  Role,
  RoutePath,
  SystemMessage,
  UserOption,
  UsersOptionName,
} from "@app/constants";
import UserService from "@app/services/http/user.service";
import { openPortalDialog } from "@app/services/modal.service";
import { CreateAdmin, CreateUser, Meta, User } from "@app/types";
import { formatDateTime } from "@app/utils/util";
import { Images } from "@assets/images";
import useCancellationRequest from "@core/hooks/use-cancellation-request.hook";
import { useCopyToClipboard } from "@core/hooks/use-copy-to-clipboard.hook";
import { useDebouncedCallback } from "@core/hooks/use-debounced-callback.hook";
import useForceUpdate from "@core/hooks/use-force-update.hook";
import { RequestParams } from "@core/services/http/http.type";
import clsx from "clsx";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function FullNameColumnTable({ data }: { data: User }) {
  return (
    <div className="flex gap-2 items-center truncate min-w-[180px] max-w-[280px]">
      <img
        className="w-9 h-9 rounded-lg"
        src={data.thumbnail}
        alt="thumbnail"
      />
      <div>{data.fullName}</div>
    </div>
  );
}

function UsernameColumnTable({ data }: { data: User }) {
  const [, copy] = useCopyToClipboard();

  const handleCopyClick = (text: string) => {
    copy(text).then(() => {
      addToast({ text: SystemMessage.COPY_SUCCESSFULLY });
    });
  };

  return (
    <div className="flex gap-2 w-[200px]">
      <div className="max-w-[180px] truncate">{data.username}</div>
      <img
        className="cursor-pointer"
        src={Images.CopyIcon.default}
        alt=""
        onClick={() => handleCopyClick(data.username)}
      />
    </div>
  );
}

function EmailColumnTable({ data }: { data: User }) {
  const [, copy] = useCopyToClipboard();

  const handleCopyClick = (text: string) => {
    copy(text).then(() => {
      addToast({ text: SystemMessage.COPY_SUCCESSFULLY });
    });
  };

  return (
    <div className="flex gap-2 w-[250px]">
      <div className="max-w-[200px] truncate">{data.email}</div>
      {!!data.email && (
        <img
          className="cursor-pointer"
          src={Images.CopyIcon.default}
          alt=""
          onClick={() => handleCopyClick(data.email)}
        />
      )}
    </div>
  );
}

function PhoneColumnTable({ data }: { data: User }) {
  const [, copy] = useCopyToClipboard();

  const handleCopyClick = (text: string) => {
    copy(text).then(() => {
      addToast({ text: SystemMessage.COPY_SUCCESSFULLY });
    });
  };

  return (
    <div className="flex gap-2 w-[150px]">
      <div className="max-w-[120px] truncate">{data.phone}</div>
      {!!data.phone && (
        <img
          className="cursor-pointer"
          src={Images.CopyIcon.default}
          alt=""
          onClick={() => handleCopyClick(data.phone)}
        />
      )}
    </div>
  );
}

function TypeColumnTable({ data }: { data: User }) {
  return (
    <div className="w-[85px]">
      <span
        className={clsx("px-4 py-2 rounded-lg text-xs", {
          "bg-[#E8FFF4] text-[#00A653]": data.isVip === UserOption.FREE,
          "bg-[#FFF0E6] text-[#FE7E20]": data.isVip === UserOption.VIP,
        })}
      >
        {UsersOptionName[data.isVip.toString()]}
      </span>
    </div>
  );
}

function DateColumnTable({ data }: { data: User }) {
  return <div>{formatDateTime(data.createdDate)}</div>;
}

function LastLoginDateColumnTable({ data }: { data: User }) {
  return <div>{formatDateTime(data.lastLoginDate)}</div>;
}

const columnConfig: ColumnConfig[] = [
  {
    label: "Họ và tên",
    dataKey: "fullName",
    width: "180px",
    component: FullNameColumnTable,
    hasOrder: true,
  },
  {
    label: "Tên đăng nhập",
    dataKey: "username",
    width: "120px",
    component: UsernameColumnTable,
    hasOrder: true,
  },
  {
    label: "Email",
    dataKey: "email",
    width: "140px",
    component: EmailColumnTable,
    hasOrder: true,
  },
  {
    label: "Số điện thoại",
    dataKey: "phone",
    width: "100px",
    component: PhoneColumnTable,
    hasOrder: true,
  },
  {
    label: "Điểm",
    dataKey: "point",
    width: "100px",
    hasOrder: true,
  },
  {
    label: "Ngày đăng nhập cuối",
    dataKey: "lastLoginDate",
    width: "180px",
    tdClassName: "truncate min-w-[180px] max-w-[180px]",
    component: LastLoginDateColumnTable,
    hasOrder: true,
  },
  {
    label: "Provider",
    dataKey: "provider",
    width: "50px",
    hasOrder: true,
  },
  {
    label: "Loại",
    dataKey: "isVip",
    width: "110px",
    hasOrder: true,
    component: TypeColumnTable,
  },
  {
    label: "Quyền",
    dataKey: "role",
    width: "150px",
    hasOrder: true,
  },
  {
    label: "Aff Alias",
    dataKey: "affAlias",
    width: "60px",
    hasOrder: true,
  },
  {
    label: "Aff Vip Time",
    dataKey: "affVipTime",
    width: "60px",
    hasOrder: true,
  },
  {
    label: "Aff Money",
    dataKey: "affMoney",
    width: "100px",
    hasOrder: true,
  },
  {
    label: "Aff Money Condition",
    dataKey: "affMoneyCondition",
    width: "100px",
    hasOrder: true,
  },
  {
    label: "Aff Percent Lv2",
    dataKey: "affPercentLv2",
    width: "100px",
    hasOrder: true,
  },
  {
    label: "Ngày tạo",
    dataKey: "createdDate",
    width: "180px",
    tdClassName: "truncate min-w-[180px] max-w-[180px]",
    component: DateColumnTable,
    hasOrder: true,
  },
];

function UserManagement() {
  const [users, setUsers] = useState<User[]>([]);
  const storedUserInfo = localStorage.getItem("app_user_info");
  const userInfo = storedUserInfo ? JSON.parse(storedUserInfo) : null;
  const navigate = useNavigate();

  const [update, forceUpdate] = useForceUpdate();

  const role = userInfo?.role;

  const [requestParams, setRequestParams] = useState<RequestParams>(
    DEFAULT_REQUEST_PARAMS
  );
  const [meta, setMeta] = useState<Meta>();

  const { abortController } = useCancellationRequest();

  useEffect(() => {
    UserService.getList(requestParams, abortController.signal).then((res) => {
      setUsers(res.data.items);
      setMeta(res.data.meta);

      if (
        requestParams.pageNum &&
        res.data.meta.totalPage > 0 &&
        res.data.meta.totalPage < requestParams.pageNum
      ) {
        const newRequestParams: RequestParams = {
          ...requestParams,
          pageNum: DEFAULT_PAGE,
        };

        setRequestParams(newRequestParams);
      }
    });
  }, [update, requestParams]);

  const onSearchChange = useDebouncedCallback((value: string) => {
    const newRequestParams: RequestParams = {
      ...requestParams,
      keyword: value,
    };

    setRequestParams(newRequestParams);
  }, DEBOUNCE_TIMER);

  const onPageChange = (page: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageNum: page };

    setRequestParams(newRequestParams);
  };

  const onPageSizeChange = (pageSize: number) => {
    const newRequestParams: RequestParams = { ...requestParams, pageSize };

    setRequestParams(newRequestParams);
  };

  const onOrderChange = (data?: Order) => {
    if (data) {
      const newRequestParams: RequestParams = {
        ...requestParams,
        sortBy: data.orderBy,
        sortType: data.order as OrderType,
      };

      setRequestParams(newRequestParams);
    }
  };

  const handleCreateAdminClick = () => {
    const adminModalObs = openPortalDialog(CreateAdminModal);

    adminModalObs.afterClosed().subscribe((data) => {
      if (data?.admin) {
        const createAdmin: CreateAdmin = {
          fullName: data?.admin.fullName,
          username: data?.admin.username,
          password: data?.admin.password,
          email: data?.admin.email,
          role: data?.admin.role,
        };

        UserService.createAdmin(createAdmin).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleCreateUserClick = () => {
    const userModalObs = openPortalDialog(CreateUserModal);

    userModalObs.afterClosed().subscribe((data) => {
      if (data?.user) {
        const createUser: CreateUser = {
          thumbnail: data?.user?.thumbnail,
          fullName: data?.user?.fullName,
          username: data?.user?.username,
          password: data?.user?.password,
          phone: data?.user?.phone,
          email: data?.user?.email,
          role: data?.user?.role,
          maxVoiceTrain: data?.user?.maxVoiceTrain,
        };

        UserService.createUser(createUser).then(() => {
          addToast({ text: SystemMessage.ADD_SUCCESSFULLY });
          forceUpdate();
        });
      }
    });
  };

  const handleDeleteClick = (userId: number) => {
    if (userId) {
      const confirmModalObs = openPortalDialog(ConfirmModal, {
        message: SystemMessage.DELETE_BANNER_NOTIFY,
      });

      confirmModalObs.afterClosed().subscribe((data) => {
        if (data?.isAccept) {
          UserService.deleteUser(userId.toString()).then(() => {
            addToast({ text: SystemMessage.DELETE_SUCCESSFULLY });
            forceUpdate();
          });
        }
      });
    }
  };

  const handleEditClick = (user: User) => {
    navigate(
      `/${RoutePath.USER_MANAGEMENT}/${RoutePath.UPDATE_USER}/${user.id}`
    );
  };

  return (
    <div className="py-6 px-12">
      <div className="flex justify-between items-center">
        <div className="text-[#343C6A]">Danh sách người dùng</div>
        <div>
          {Role.ROOT === role && (
            <button
              className="mr-4 text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
              onClick={handleCreateAdminClick}
            >
              <div className="flex gap-4">
                <img src={Images.PlusIcon.default} alt="" />
                <div>Admin</div>
              </div>
            </button>
          )}
          <button
            className="text-xl px-6 rounded-xl leading-14 h-14 bg-color-primary hover:bg-[#667ffc] text-white"
            onClick={handleCreateUserClick}
          >
            <div className="flex gap-4">
              <img src={Images.PlusIcon.default} alt="" />
              <div>User/Author/Admin Root</div>
            </div>
          </button>
        </div>
      </div>
      <div className="mt-6 rounded-xl bg-white py-6 px-5">
        <TitleDescription
          title="Danh sách người dùng"
          onInputChange={onSearchChange}
        />
        <div className="mt-6 overflow-x-auto">
          <Table
            columnConfig={columnConfig}
            dataTable={users}
            meta={meta}
            hasAction
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClickOrder={onOrderChange}
            onEditClick={handleEditClick}
            onDeleteClick={handleDeleteClick}
            tableClassName="w-[2200px]"
            componentEmpty={
              <div className="text-center py-3 bg-[#f4f4f4]">
                <p className="text-[#666] text-xl">Không có dữ liệu</p>
              </div>
            }
          />
        </div>
      </div>
    </div>
  );
}

export default UserManagement;
